import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Snackbar,
} from "@mui/material";
import {
  Webhook as WebhookIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import TradingViewWebhookService from "../../services/TradingViewWebhookService";

function TradingViewWebhook() {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [signals, setSignals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [brokerStatus, setBrokerStatus] = useState(null);

  // Configuration state
  const [config, setConfig] = useState({
    enabled: true,
    secret_key: "",
    allowed_symbols: [],
    max_daily_trades: 50,
    require_confirmation: false,
    auto_execute: true,
    risk_management: {
      max_risk_per_trade: 2.0,
      max_daily_loss: 5.0,
      max_position_size_percent: 10.0,
    },
  });

  // UI state
  const [showSecret, setShowSecret] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [connectBrokerDialog, setConnectBrokerDialog] = useState(false);
  const [sampleAlertDialog, setSampleAlertDialog] = useState(false);

  // Broker connection form
  const [brokerForm, setBrokerForm] = useState({
    broker_id: "",
    broker_type: "paper_trading",
    api_key: "",
    api_secret: "",
    paper: true,
  });

  // Get the webhook URL
  const webhookUrl = `${window.location.origin}/api/tradingview/webhook`;

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusData, signalsData, tradesData, brokersData, configData] =
        await Promise.all([
          TradingViewWebhookService.getStatus(),
          TradingViewWebhookService.getSignals(20),
          TradingViewWebhookService.getTrades(20),
          TradingViewWebhookService.listBrokers(),
          TradingViewWebhookService.getConfig(),
        ]);

      setWebhookStatus(statusData);
      setSignals(signalsData.signals || []);
      setTrades(tradesData.trades || []);
      setBrokers(brokersData.brokers || []);
      setConfig(configData);

      // Get broker status if there's a default broker
      if (brokersData.default_broker) {
        const brokerStatusData =
          await TradingViewWebhookService.getBrokerStatus();
        setBrokerStatus(brokerStatusData);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Error loading webhook data", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    showSnackbar("Webhook URL copied to clipboard");
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(config.secret_key);
    showSnackbar("Secret key copied to clipboard");
  };

  const handleGenerateSecret = () => {
    const newSecret = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    setConfig({ ...config, secret_key: newSecret });
    showSnackbar("New secret key generated");
  };

  const handleSaveConfig = async () => {
    try {
      await TradingViewWebhookService.updateConfig(config);
      showSnackbar("Configuration saved successfully");
    } catch (error) {
      showSnackbar("Error saving configuration", "error");
    }
  };

  const handleTestWebhook = async () => {
    try {
      const result = await TradingViewWebhookService.testWebhook();
      if (result.success) {
        showSnackbar("Webhook is active and ready!");
      }
    } catch (error) {
      showSnackbar("Webhook test failed", "error");
    }
  };

  const handleConnectBroker = async () => {
    try {
      await TradingViewWebhookService.connectBroker(brokerForm.broker_id, {
        broker_type: brokerForm.broker_type,
        api_key: brokerForm.api_key,
        api_secret: brokerForm.api_secret,
        paper: brokerForm.paper,
      });
      showSnackbar(`Connected to ${brokerForm.broker_type}`);
      setConnectBrokerDialog(false);
      loadData();
    } catch (error) {
      showSnackbar("Failed to connect broker", "error");
    }
  };

  const handleDisconnectBroker = async (brokerId) => {
    try {
      await TradingViewWebhookService.disconnectBroker(brokerId);
      showSnackbar("Broker disconnected");
      loadData();
    } catch (error) {
      showSnackbar("Failed to disconnect broker", "error");
    }
  };

  const getSampleAlert = () => {
    return TradingViewWebhookService.generateSampleAlert("buy", "BTCUSDT", {
      stopLoss: 40000,
      takeProfit: 50000,
      riskPercent: 2,
      strategyName: "My TradingView Strategy",
      passphrase: config.secret_key || "your_webhook_secret",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading webhook data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3, p: 2, borderRadius: 3,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: 'wrap', gap: 1.5,
          background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <WebhookIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            TradingView Webhook
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleTestWebhook}
          >
            Test Webhook
          </Button>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                {webhookStatus?.status === "active" ? (
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                ) : (
                  <ErrorIcon color="error" sx={{ mr: 1 }} />
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  Webhook Status
                </Typography>
              </Box>
              <Typography variant="h5">
                {webhookStatus?.status === "active" ? "Active" : "Inactive"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Signals Received
              </Typography>
              <Typography variant="h5">
                {webhookStatus?.total_signals_received || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Trades Executed
              </Typography>
              <Typography variant="h5">
                {webhookStatus?.total_trades_executed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Connected Brokers
              </Typography>
              <Typography variant="h5">{brokers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
        >
          <Tab icon={<SettingsIcon />} label="Configuration" />
          <Tab icon={<AccountBalanceIcon />} label="Broker Connections" />
          <Tab icon={<HistoryIcon />} label="Signal History" />
          <Tab icon={<CodeIcon />} label="Setup Guide" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Webhook URL */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <LinkIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Webhook URL
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Use this URL in your TradingView alert webhook settings
              </Alert>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  value={webhookUrl}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Copy URL">
                  <IconButton onClick={handleCopyWebhookUrl}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </Grid>

          {/* Secret Key */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Secret Key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add this passphrase to your TradingView alert message for
                security
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <TextField
                  fullWidth
                  type={showSecret ? "text" : "password"}
                  value={config.secret_key}
                  onChange={(e) =>
                    setConfig({ ...config, secret_key: e.target.value })
                  }
                />
                <IconButton onClick={() => setShowSecret(!showSecret)}>
                  {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <Tooltip title="Copy">
                  <IconButton onClick={handleCopySecret}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Button variant="outlined" onClick={handleGenerateSecret}>
                Generate New Secret
              </Button>
            </Paper>
          </Grid>

          {/* Settings */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Webhook Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) =>
                      setConfig({ ...config, enabled: e.target.checked })
                    }
                  />
                }
                label="Enable Webhook"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.auto_execute}
                    onChange={(e) =>
                      setConfig({ ...config, auto_execute: e.target.checked })
                    }
                  />
                }
                label="Auto-Execute Trades"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.require_confirmation}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        require_confirmation: e.target.checked,
                      })
                    }
                  />
                }
                label="Require Confirmation"
              />
              <TextField
                fullWidth
                type="number"
                label="Max Daily Trades"
                value={config.max_daily_trades}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    max_daily_trades: parseInt(e.target.value),
                  })
                }
                sx={{ mt: 2 }}
              />
            </Paper>
          </Grid>

          {/* Risk Management */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Risk Management
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Risk Per Trade (%)"
                    value={config.risk_management.max_risk_per_trade}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        risk_management: {
                          ...config.risk_management,
                          max_risk_per_trade: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Daily Loss (%)"
                    value={config.risk_management.max_daily_loss}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        risk_management: {
                          ...config.risk_management,
                          max_daily_loss: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Position Size (%)"
                    value={config.risk_management.max_position_size_percent}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        risk_management: {
                          ...config.risk_management,
                          max_position_size_percent: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" onClick={handleSaveConfig}>
                  Save Configuration
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Connected Brokers */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Connected Brokers</Typography>
                <Button
                  variant="contained"
                  onClick={() => setConnectBrokerDialog(true)}
                >
                  Connect Broker
                </Button>
              </Box>

              {brokers.length === 0 ? (
                <Alert severity="warning">
                  <AlertTitle>No Brokers Connected</AlertTitle>
                  Connect a broker to start executing trades from TradingView
                  signals. Paper trading mode will be used by default.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Broker ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Default</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {brokers.map((broker) => (
                        <TableRow key={broker.broker_id}>
                          <TableCell>{broker.broker_id}</TableCell>
                          <TableCell>
                            <Chip label={broker.broker_type} size="small" />
                          </TableCell>
                          <TableCell>
                            {broker.is_connected ? (
                              <Chip
                                label="Connected"
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Disconnected"
                                color="error"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {broker.is_default && (
                              <Chip
                                label="Default"
                                color="primary"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDisconnectBroker(broker.broker_id)
                              }
                            >
                              Disconnect
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Portfolio Status */}
          {brokerStatus && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Portfolio Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Cash Balance
                        </Typography>
                        <Typography variant="h5">
                          ${brokerStatus.balance?.cash?.toLocaleString() || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Value
                        </Typography>
                        <Typography variant="h5">
                          $
                          {brokerStatus.balance?.total_value?.toLocaleString() ||
                            0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Today's Trades
                        </Typography>
                        <Typography variant="h5">
                          {brokerStatus.daily_stats?.trades_count || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          {/* Recent Signals */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Signals
              </Typography>
              {signals.length === 0 ? (
                <Typography color="text.secondary">
                  No signals received yet
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {signals.map((signal, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(signal.received_at).toLocaleTimeString()}
                          </TableCell>
                          <TableCell>{signal.alert?.symbol}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={signal.alert?.action?.toUpperCase()}
                              color={
                                signal.alert?.action === "buy"
                                  ? "success"
                                  : "error"
                              }
                              icon={
                                signal.alert?.action === "buy" ? (
                                  <TrendingUpIcon />
                                ) : (
                                  <TrendingDownIcon />
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={signal.status}
                              color={
                                signal.status === "executed"
                                  ? "success"
                                  : "default"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Recent Trades */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Trades
              </Typography>
              {trades.length === 0 ? (
                <Typography color="text.secondary">
                  No trades executed yet
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Side</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trades.map((trade, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(trade.executed_at).toLocaleTimeString()}
                          </TableCell>
                          <TableCell>{trade.symbol}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={trade.action?.toUpperCase()}
                              color={
                                trade.action === "buy" ? "success" : "error"
                              }
                            />
                          </TableCell>
                          <TableCell>{trade.quantity}</TableCell>
                          <TableCell>
                            ${trade.executed_price?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                TradingView Setup Guide
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                Follow these steps to connect TradingView alerts to your trading
                system
              </Alert>

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Step 1: Copy Webhook URL
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Copy the webhook URL above and paste it in your TradingView
                  alert's "Webhook URL" field.
                </Typography>
                <TextField
                  fullWidth
                  value={webhookUrl}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Step 2: Configure Alert Message
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  In TradingView, set your alert message to JSON format with the
                  following structure:
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setSampleAlertDialog(true)}
                  sx={{ mb: 2 }}
                >
                  View Sample Alert Message
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Step 3: Connect Your Broker
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Go to the "Broker Connections" tab and connect your trading
                  account (Binance, Alpaca, etc.) or use Paper Trading mode for
                  testing.
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Supported Alert Actions
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Action</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Chip label="buy" color="success" size="small" />
                        </TableCell>
                        <TableCell>Open a long position</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Chip label="sell" color="error" size="small" />
                        </TableCell>
                        <TableCell>
                          Open a short position or close a long
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Chip label="close" size="small" />
                        </TableCell>
                        <TableCell>
                          Close all positions for the symbol
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Chip label="close_long" size="small" />
                        </TableCell>
                        <TableCell>Close long positions only</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Chip label="close_short" size="small" />
                        </TableCell>
                        <TableCell>Close short positions only</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Connect Broker Dialog */}
      <Dialog
        open={connectBrokerDialog}
        onClose={() => setConnectBrokerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect Broker</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Broker ID"
                value={brokerForm.broker_id}
                onChange={(e) =>
                  setBrokerForm({ ...brokerForm, broker_id: e.target.value })
                }
                helperText="A unique identifier for this connection"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Broker Type</InputLabel>
                <Select
                  value={brokerForm.broker_type}
                  label="Broker Type"
                  onChange={(e) =>
                    setBrokerForm({
                      ...brokerForm,
                      broker_type: e.target.value,
                    })
                  }
                >
                  <MenuItem value="paper_trading">
                    Paper Trading (Test Mode)
                  </MenuItem>
                  <MenuItem value="binance">Binance Spot</MenuItem>
                  <MenuItem value="binance_futures">Binance Futures</MenuItem>
                  <MenuItem value="alpaca">Alpaca (Stocks)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {brokerForm.broker_type !== "paper_trading" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={brokerForm.api_key}
                    onChange={(e) =>
                      setBrokerForm({ ...brokerForm, api_key: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="API Secret"
                    value={brokerForm.api_secret}
                    onChange={(e) =>
                      setBrokerForm({
                        ...brokerForm,
                        api_secret: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            )}
            {brokerForm.broker_type === "alpaca" && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={brokerForm.paper}
                      onChange={(e) =>
                        setBrokerForm({
                          ...brokerForm,
                          paper: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Paper Trading Mode"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectBrokerDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConnectBroker}>
            Connect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sample Alert Dialog */}
      <Dialog
        open={sampleAlertDialog}
        onClose={() => setSampleAlertDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sample TradingView Alert Message</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Copy this JSON format into your TradingView alert message:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: "grey.900" }}>
            <pre style={{ margin: 0, color: "#fff", overflow: "auto" }}>
              {getSampleAlert()}
            </pre>
          </Paper>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Dynamic Variables</AlertTitle>
            You can use TradingView's built-in variables like {
              "{{ticker}}"
            }, {"{{close}}"}, {"{{strategy.order.action}}"} etc.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSampleAlertDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              navigator.clipboard.writeText(getSampleAlert());
              showSnackbar("Alert message copied to clipboard");
            }}
          >
            Copy to Clipboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TradingViewWebhook;
